import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { API_BASE_URL } from "../../App";
import "./BookDemoModal.css";

type BookDemoModalProps = {
    showModal: boolean;
    onModalClose: () => void;
};

export function BookDemoModal({ showModal, onModalClose }: BookDemoModalProps) {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    const trimmedEmail = useMemo(() => email.trim(), [email]);
    const trimmedMessage = useMemo(() => message.trim(), [message]);

    const canSend = trimmedEmail.length > 0 && trimmedMessage.length > 0 && !isSending;

    useEffect(() => {
        if (!showModal) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onModalClose();
        };

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [showModal, onModalClose]);

    useEffect(() => {
        if (!showModal) {
            setEmail("");
            setMessage("");
            setIsSending(false);
        }
    }, [showModal]);

    if (!showModal) return null;

    const sendDemoRequest = async (email: string, message: string) => {
        const bookDemoUrl = `${API_BASE_URL}/webapp/demo`;
        await fetch(bookDemoUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, message }),
        });
    };

    const handleCancel = () => {
        if (isSending) return;
        onModalClose();
    };

    const handleSend = async () => {
        if (!canSend) return; // safety
        setIsSending(true);
        try {
            await sendDemoRequest(trimmedEmail, trimmedMessage);
        } finally {
            setIsSending(false);
            onModalClose();
        }
    };

    return createPortal(
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={handleCancel}>
            <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">Book a Demo</h2>

                    <button className="modal-close" type="button" onClick={handleCancel} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    <label className="modal-label" htmlFor="demo-email">
                        Email
                    </label>
                    <input
                        id="demo-email"
                        className="modal-input"
                        type="email"
                        placeholder="Your email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSending}
                    />

                    <label className="modal-label" htmlFor="demo-message">
                        Message
                    </label>
                    <textarea
                        id="demo-message"
                        className="modal-textarea"
                        placeholder="Send us a message, we'll get back to you"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isSending}
                        rows={5}
                    />
                </div>

                <div className="modal-actions">
                    <button
                        className="modal-btn modal-btn-secondary"
                        type="button"
                        onClick={handleCancel}
                        disabled={isSending}
                    >
                        Cancel
                    </button>

                    <button
                        className="modal-btn modal-btn-primary"
                        type="button"
                        onClick={handleSend}
                        disabled={!canSend}
                    >
                        {isSending ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
